/**
 * V6-011 砺锋台 V2 — 战斗观看层
 *
 * P0 阶段：mock 战斗，简化展示（文字 + 进度条 + 1s 间隔）
 * P1 阶段：替换为真实 Pyodide 战斗 + Canvas 4x 加速渲染
 *
 * @see design/v6-sharpen/gdd-v6-010-sharpen.md §8 §4.2
 * @date 2026-07-20
 */

import type { LevelEntry, OpenType } from "./types";
import { TYPE_LABELS } from "./types";
import { mockBattle } from "./mock-data";

/** 单场战斗原始结果 */
export interface BattleResult {
  level: LevelEntry;
  result: "win" | "loss";
  hpPct: number;
  timeMs: number;
}

/** 战斗观看层回调 */
export interface BattleViewerCallbacks {
  onComplete: (battles: BattleResult[]) => void;
  onSkip: () => void;
}

/** 战斗观看层状态 */
interface ViewerState {
  levels: LevelEntry[];
  currentIndex: number;
  battles: BattleResult[];
  isPlaying: boolean;
  isSkipped: boolean;
}

/**
 * V6-011: 简化签名（levels + callbacks），不再需要 container/type/rankIndex
 *
 * 流程：
 * 1. 挂载到 document.body（fixed overlay）
 * 2. 逐场 mock 战斗（每场 ~1.5s）
 * 3. 用户可"跳过本场"或"跳过全部"
 * 4. onComplete 回写通关数据
 */
export function startBattleViewer(
  levels: LevelEntry[],
  callbacks: BattleViewerCallbacks
): void {
  if (levels.length === 0) {
    callbacks.onComplete([]);
    return;
  }

  const type: OpenType = levels[0].type;

  const state: ViewerState = {
    levels,
    currentIndex: 0,
    battles: [],
    isPlaying: true,
    isSkipped: false,
  };

  // V6-011: 挂载到 document.body（fixed overlay）
  const overlay = document.createElement("div");
  overlay.className = "s10-viewer-overlay";
  overlay.innerHTML = renderViewerInner(type, levels.length);
  document.body.appendChild(overlay);
  attachViewerEvents(overlay, state, callbacks);

  // 开始第 1 场
  playBattle(overlay, state, callbacks);
}

/** 渲染观看层内部（不含 overlay 壳） */
function renderViewerInner(type: OpenType, totalBattles: number): string {
  const typeLabel = TYPE_LABELS[type];
  return `<div class="s10-viewer">
    <div class="s10-viewer-header">
      <div class="s10-viewer-title">自动化对打 · ${typeLabel}</div>
      <div class="s10-viewer-actions">
        <button class="s10-viewer-btn" data-action="skip-one">跳过本场</button>
        <button class="s10-viewer-btn" data-action="skip-all">跳过全部</button>
      </div>
    </div>
    <div class="s10-viewer-body" id="s10-viewer-body">
      <!-- 单场内容由 playBattle 动态注入 -->
    </div>
    <div class="s10-viewer-progress" id="s10-viewer-progress">
      ${Array.from({ length: totalBattles }, (_, i) =>
        `<div class="s10-viewer-progress-dot" data-index="${i}"></div>`
      ).join("")}
    </div>
  </div>`;
}

/** 播放单场战斗 */
function playBattle(
  container: HTMLElement,
  state: ViewerState,
  callbacks: BattleViewerCallbacks
): void {
  if (state.isSkipped || state.currentIndex >= state.levels.length) {
    finishViewer(container, state, callbacks);
    return;
  }

  const level = state.levels[state.currentIndex];
  const body = container.querySelector("#s10-viewer-body") as HTMLElement;
  const progressDots = container.querySelectorAll(".s10-viewer-progress-dot");

  // 更新进度点
  progressDots.forEach((dot, i) => {
    dot.classList.remove("active", "done");
    if (i < state.currentIndex) dot.classList.add("done");
    else if (i === state.currentIndex) dot.classList.add("active");
  });

  // 渲染单场骨架
  body.innerHTML = renderSingleBattleShell(level, state.currentIndex + 1, state.levels.length);

  // P0 mock：1.5s 后出结果（模拟 4x 加速观看）
  const battleDuration = 1500;

  setTimeout(() => {
    if (state.isSkipped) return;

    // 生成战斗结果（根据关卡难度决定胜率）
    const battle = mockBattle(level);
    const result: BattleResult = { level, result: battle.result, hpPct: battle.hpPct, timeMs: battle.timeMs };
    state.battles.push(result);

    // 显示结果
    body.innerHTML = renderSingleBattleResult(level, battle.result, battle.hpPct, battle.timeMs, state.currentIndex + 1, state.levels.length);

    // 1s 间隔后进入下一场
    setTimeout(() => {
      if (state.isSkipped) return;
      state.currentIndex++;
      playBattle(container, state, callbacks);
    }, 1000);
  }, battleDuration);
}

/** 渲染单场骨架（进行中） */
function renderSingleBattleShell(level: LevelEntry, current: number, total: number): string {
  return `<div class="s10-battle">
    <div class="s10-battle-header">
      <span class="s10-battle-index">第 ${current}/${total} 场</span>
      <span class="s10-battle-level">${level.name}</span>
      <span class="s10-battle-ai-level">★${level.difficultyStars}</span>
    </div>
    <div class="s10-battle-arena">
      <div class="s10-battle-arena-text">
        <div class="s10-battle-loading">战斗进行中（4x 加速）...</div>
        <div class="s10-battle-desc">${level.id} · ${level.type} · ${level.mapSize}x${level.mapSize}</div>
      </div>
      <div class="s10-battle-arena-progress">
        <div class="s10-battle-arena-bar"></div>
      </div>
    </div>
  </div>`;
}

/** 渲染单场结果 */
function renderSingleBattleResult(
  level: LevelEntry,
  result: "win" | "loss",
  hpPct: number,
  timeMs: number,
  current: number,
  total: number
): string {
  const win = result === "win";
  const resultClass = win ? "s10-battle-result--win" : "s10-battle-result--loss";
  const resultText = win ? "胜" : "负";
  const timeSec = (timeMs / 1000).toFixed(1);
  const exp = win ? 1 : 0;

  return `<div class="s10-battle s10-battle--done">
    <div class="s10-battle-header">
      <span class="s10-battle-index">第 ${current}/${total} 场</span>
      <span class="s10-battle-level">${level.name}</span>
      <span class="s10-battle-ai-level">★${level.difficultyStars}</span>
    </div>
    <div class="s10-battle-result ${resultClass}">
      <div class="s10-battle-result-text">${resultText}</div>
      <div class="s10-battle-result-stats">
        <span>HP ${hpPct}%</span>
        <span>用时 ${timeSec}s</span>
        <span>+${exp} EXP</span>
      </div>
    </div>
  </div>`;
}

/** 渲染最终结果摘要 */
function renderSummary(battles: BattleResult[]): string {
  const wins = battles.filter(b => b.result === "win").length;
  const totalExp = battles.filter(b => b.result === "win").length;
  const winText = wins === battles.length ? "全胜！" : wins === 0 ? "全负" : `${wins} 胜 ${battles.length - wins} 负`;

  return `<div class="s10-summary">
    <div class="s10-summary-title">本轮结果</div>
    <div class="s10-summary-result">${winText}</div>
    <div class="s10-summary-exp">获得 ${totalExp} EXP</div>
    <div class="s10-summary-list">
      ${battles.map((b, i) => {
        const win = b.result === "win";
        const timeSec = (b.timeMs / 1000).toFixed(1);
        return `<div class="s10-summary-item ${win ? "s10-summary-item--win" : "s10-summary-item--loss"}">
          <span>#${i + 1}</span>
          <span>${b.level.name}</span>
          <span>${win ? "胜" : "负"}</span>
          <span>HP ${b.hpPct}%</span>
          <span>${timeSec}s</span>
          <span>+${win ? 1 : 0} EXP</span>
        </div>`;
      }).join("")}
    </div>
    <button class="s10-summary-btn" data-action="return">返回</button>
  </div>`;
}

/** 完成观看，显示摘要 */
function finishViewer(
  container: HTMLElement,
  state: ViewerState,
  callbacks: BattleViewerCallbacks
): void {
  const body = container.querySelector("#s10-viewer-body") as HTMLElement;
  body.innerHTML = renderSummary(state.battles);

  // 隐藏进度点
  const progress = container.querySelector(".s10-viewer-progress") as HTMLElement;
  if (progress) progress.style.display = "none";

  // 隐藏跳过按钮
  const actions = container.querySelector(".s10-viewer-actions") as HTMLElement;
  if (actions) actions.style.display = "none";

  // 绑定返回按钮
  const returnBtn = container.querySelector('[data-action="return"]') as HTMLButtonElement;
  if (returnBtn) {
    returnBtn.addEventListener("click", () => {
      // V6-011: 关闭后移除 overlay
      container.remove();
      callbacks.onComplete(state.battles);
    });
  }
}

/** 绑定观看层事件 */
function attachViewerEvents(
  container: HTMLElement,
  state: ViewerState,
  callbacks: BattleViewerCallbacks
): void {
  container.addEventListener("click", (e) => {
    const target = e.target as HTMLElement;
    const action = target.getAttribute("data-action");
    if (action === "skip-one") {
      // 跳过本场：直接生成结果，进入下一场
      if (state.currentIndex < state.levels.length) {
        const level = state.levels[state.currentIndex];
        const battle = mockBattle(level);
        state.battles.push({ level, result: battle.result, hpPct: battle.hpPct, timeMs: battle.timeMs });
        state.currentIndex++;
        playBattle(container, state, callbacks);
      }
    } else if (action === "skip-all") {
      // 跳过全部：生成所有剩余场的结果
      state.isSkipped = true;
      while (state.currentIndex < state.levels.length) {
        const level = state.levels[state.currentIndex];
        const battle = mockBattle(level);
        state.battles.push({ level, result: battle.result, hpPct: battle.hpPct, timeMs: battle.timeMs });
        state.currentIndex++;
      }
      finishViewer(container, state, callbacks);
    }
  });
}
